import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Send, Loader2, ChevronsUpDown, Check, X } from "lucide-react";
import { gsap } from "gsap";
import toast from "react-hot-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandGroup, CommandItem } from "./ui/command";
import { cn } from "@/lib/utils";
import { InputNew } from "./ui/inputNew";
import { TextareaNew } from "./ui/textareaNew";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

const ContactUsPage = () => {
  const submitBtnRef = useRef<HTMLButtonElement | null>(null);
  const textRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const text = textRef.current;

    if (!text) return;

    gsap.set([text], {
      opacity: 0,
      y: 50,
    });

    const tl = gsap.timeline({ delay: 0.5 });

    tl.to(text, {
      opacity: 1,
      y: 0,
      duration: 1,
      ease: "power3.out",
    });
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    services: [] as string[],
    budget: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const servicesList = [
    "Content Creation",
    "Digital Marketing",
    "Social Media Handling",
    "WhatsApp Bulk Messaging",
    "Tech Solution",
    "Graphic Designing",
    "Web & App Development",
    "SEO Optimization",
    "Media Solution",
  ];

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading) return;
    if (formData.services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }

    setLoading(true);

    const url =
      "https://script.google.com/macros/s/AKfycbzPEYH6mOngeAuZsJtJ5VjCqKsIkS8vJQaUNWZJaD6FCrcRPtim0Y_11sBs6aOgfUBt/exec";

    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        services: formData.services.join(", "),
        budget: formData.budget,
        message: formData.message,
      });

      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const data = await res.text();

      if (res.ok) {
        toast.success("Our team will get in touch with you within 24 hours.");
        setFormData({
          name: "",
          email: "",
          phone: "",
          services: [],
          budget: "",
          message: "",
        });
      } else {
        throw new Error(data || "Something went wrong");
      }
    } catch (error: any) {
      toast.error("Failed to submit. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }

    if (submitBtnRef.current) {
      gsap.to(submitBtnRef.current, {
        scale: 0.95,
        duration: 0.1,
        yoyo: true,
        repeat: 1,
        ease: "power2.out",
      });
    }
  };

  return (
    <div
      ref={textRef}
      className="bg-transparent max-h-[90vh] overflow-y-auto px-5 lg:px-14 w-full md:w-[80%] xl:w-[1200px]"
    >
      <h2 className="text-5xl text-[#0d1821] mb-8 font-medium ">
        Ready
        <div className="pt-[6px]" />
        <span className="text-[#00a878]">to Grow ?</span>
      </h2>
      <p className="mb-4 text-md md:text-lg text-[#0d1821]/60 mx-auto font-poppins">
        Please share your requirements or any questions you have in the message
        section.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5 md:space-y-8">
        <div className="grid gap-6 md:gap-8">
          <div className="form-field">
            <InputNew
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Name *"
            />
          </div>

          <div className="form-field">
            <InputNew
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Email"
            />
          </div>

          <div className="form-field">
            <InputNew
              id="phone"
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="Phone *"
            />
          </div>
        </div>

        {/* Services */}
        <div className="form-field">
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex items-center justify-between bg-[#f5f5f5] w-full text-md font-poppins text-[#0d1821]/70 pr-3 py-2 border border-t-0 border-l-0 border-r-0 rounded-none border-b-[#0d1821]/40 cursor-pointer min-h-[42px] ">
                <div className="flex flex-wrap gap-3">
                  {formData.services.length > 0 ? (
                    formData.services.map((service) => (
                      <span
                        key={service}
                        className="flex items-center gap-2 bg-[#00a878]/10 text-[#0d1821] px-2 py-1 rounded-full text-xs"
                      >
                        {service}
                        <X
                          className="h-3 w-3 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData((prev) => ({
                              ...prev,
                              services: prev.services.filter(
                                (s) => s !== service
                              ),
                            }));
                          }}
                        />
                      </span>
                    ))
                  ) : (
                    <span className="text-[#0d1821]/70">
                      Services Required *
                    </span>
                  )}
                </div>
                <ChevronsUpDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
              </div>
            </PopoverTrigger>

            <PopoverContent className="p-0 font-poppins w-[350px] sm:w-[450px] md:w-[550px] lg:w-[1100px]">
              <Command className="w-full">
                <CommandGroup className="w-full">
                  {servicesList.map((service) => {
                    const isSelected = formData.services.includes(service);
                    return (
                      <CommandItem
                        key={service}
                        onSelect={() => {
                          setFormData((prev) => {
                            const exists = prev.services.includes(service);
                            return {
                              ...prev,
                              services: exists
                                ? prev.services.filter((s) => s !== service)
                                : [...prev.services, service],
                            };
                          });
                        }}
                        className="cursor-pointer"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4 ",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {service}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Budget */}
        <div className="form-field">
          <Select
            value={formData.budget}
            onValueChange={(value) =>
              setFormData((prev) => ({ ...prev, budget: value }))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Budget range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Under ₹10k">Under ₹10k</SelectItem>
              <SelectItem value="₹10k–25k">₹10k–25k</SelectItem>
              <SelectItem value="₹25k–50k">₹25k–50k</SelectItem>
              <SelectItem value="₹50k+">₹50k+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Requirement */}
        <div className="form-field">
          <TextareaNew
            id="message"
            name="message"
            rows={window.innerWidth < 500 ? 3 : window.innerWidth < 700 ? 3 : 4}
            value={formData.message}
            onChange={handleInputChange}
            placeholder="Message"
          />
        </div>

        <Button
          ref={submitBtnRef}
          type="submit"
          disabled={loading}
          className="submit-btn btn-hero w-full pt-3 text-lg font-medium group text-[#0d1821] disabled:opacity-70"
        >
          {loading ? (
            <Loader2 className="mr-2 w-5 h-5 animate-spin text-[#0d1821]" />
          ) : (
            <Send className="mr-2 w-5 h-5 group-hover:translate-x-1 transition-transform text-[#0d1821]" />
          )}
          {loading ? "Submitting..." : "Send Message"}
        </Button>
      </form>
    </div>
  );
};

export default ContactUsPage;
